import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const cookieContent = {
  en: {
    title: 'Cookie Policy',
    lastUpdated: 'January 2023',
    sections: [
      {
        title: '1. What Are Cookies',
        content: `Cookies are small pieces of text sent by your web browser by a website you visit. A cookie file is stored in your web browser and allows the Service or a third-party to recognize you and make your next visit easier and the Service more useful to you.

Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your personal computer or mobile device when you go offline, while session cookies are deleted as soon as you close your web browser.

When you access our website, we may place a number of cookies in your browser. We use cookies for the following purposes: to enable certain functions of the service, to provide analytics, to store your preferences, and to enable advertisements delivery.`
      },
      {
        title: '2. How We Use Cookies',
        content: `We use both session and persistent cookies on the Service and we use different types of cookies to run the Service:

Essential Cookies:
We may use essential cookies to authenticate users and prevent fraudulent use of user accounts. These cookies are strictly necessary to provide you with services available through our website and to use some of its features.

Analytics Cookies:
We may use analytics cookies to track information about how the Service is used so that we can make improvements. We may also use analytics cookies to test new advertisements, pages, features or new functionality of the Service to see how our users react to them.

Preference Cookies:
We may use preference cookies to remember your preferences and various settings. These include your language preferences, country settings, and other customization options.

Marketing Cookies:
We may use marketing cookies to deliver advertisements that are relevant to you. Marketing cookies track your activity across websites to help advertisers deliver more relevant advertising or to limit how many times you see an ad.`
      },
      {
        title: '3. Types of Cookies We Use',
        content: `The following types of cookies may be used when you visit our website:

Strictly Necessary Cookies:
These cookies are essential for you to browse the website and use its features, such as accessing secure areas of the site. Without these cookies, services you have asked for cannot be provided.
Examples: Session management, security tokens, load balancing

Performance Cookies:
These cookies collect information about how you use our website—for instance, which pages you go to most often. This data may be used to help optimize our website and make it easier for you to navigate.
Examples: Google Analytics, page load times, error tracking

Functionality Cookies:
These cookies allow the website to remember choices you make (such as your language preference) and provide enhanced, more personal features.
Examples: Language selection, user interface customization, remembering login details

Targeting/Advertising Cookies:
These cookies are used to deliver adverts more relevant to you and your interests. They are also used to limit the number of times you see an advertisement as well as help measure the effectiveness of advertising campaigns.
Examples: Social media tracking, remarketing cookies, conversion tracking`
      },
      {
        title: '4. Third-Party Cookies',
        content: `In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the Service, deliver advertisements on and through the Service, and so on.

Third-party services we use that may place cookies include:

Google Analytics:
We use Google Analytics to analyze the use of our website. Google Analytics gathers information about website use by means of cookies. The information gathered relating to our website is used to create reports about the use of our website.

Social Media Platforms:
Our website may include social media features, such as the Facebook Like button. These features may collect your IP address, which page you are visiting on our site, and may set a cookie to enable the feature to function properly.

Payment Processors:
When you make a booking, our payment processor may set cookies to enable the payment functionality and prevent fraud.

Marketing Partners:
We may work with advertising and marketing partners who may set cookies on our website to help us deliver relevant advertisements to you.`
      },
      {
        title: '5. Managing Your Cookie Preferences',
        content: `You have several options to manage cookies:

Browser Settings:
Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience, as it will no longer be personalized to you.

You can find out how to manage cookies in your browser here:
• Chrome: chrome://settings/cookies
• Firefox: about:preferences#privacy
• Safari: Preferences > Privacy
• Edge: Settings > Cookies and site permissions

Opt-Out Tools:
You can opt out of interest-based advertising by visiting:
• Digital Advertising Alliance: http://optout.aboutads.info/
• Network Advertising Initiative: http://optout.networkadvertising.org/
• European Interactive Digital Advertising Alliance: http://www.youronlinechoices.eu/

Do Not Track:
Some browsers include the ability to transmit "Do Not Track" signals. We do our best to honor these signals where technically feasible.

Cookie Consent:
When you first visit our website, you will be asked to consent to our use of cookies. You can withdraw your consent at any time by clearing your cookies and revisiting our site.`
      },
      {
        title: '6. Specific Cookies We Use',
        content: `Here is a detailed list of the cookies we use on our website:

Essential Cookies:
• PHPSESSID - Session identifier (Session cookie)
• csrf_token - Security token to prevent cross-site request forgery (Session cookie)
• cookie_consent - Stores your cookie preferences (1 year)

Analytics Cookies:
• _ga - Google Analytics identifier (2 years)
• _gid - Google Analytics identifier (24 hours)
• _gat - Used to throttle request rate (1 minute)

Preference Cookies:
• language - Stores your language preference (1 year)
• currency - Stores your currency preference (1 year)

Marketing Cookies:
• _fbp - Facebook pixel for advertising (3 months)
• IDE - Google DoubleClick advertising (1 year)`
      },
      {
        title: '7. Cookie Duration',
        content: `The length of time that a cookie remains on your device depends on whether it is a "persistent" or "session" cookie:

Session Cookies:
Session cookies last only as long as your browser is open. They are deleted automatically when you close your browser.

Persistent Cookies:
Persistent cookies survive after your browser is closed. They can be used by the website to recognize your device when you open your browser and browse the Internet again. The duration of persistent cookies is set by the website and can vary from a few minutes to several years.

We set cookies with varying expiration periods:
• Some cookies expire when you close your browser
• Some cookies expire after 24 hours
• Some cookies may last up to 2 years
• You can delete cookies at any time through your browser settings`
      },
      {
        title: '8. Updates to This Policy',
        content: `We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons.

Please therefore revisit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.

The date at the top of this Cookie Policy indicates when it was last updated.

If we make material changes to this policy, we will notify you through a notice on our website homepage or by other appropriate means.`
      },
      {
        title: '9. More Information About Cookies',
        content: `If you would like to learn more about cookies in general and how to manage them, visit the following resources:

• All About Cookies: https://www.allaboutcookies.org/
• Your Online Choices: https://www.youronlinechoices.com/
• Network Advertising Initiative: https://www.networkadvertising.org/

These resources provide comprehensive information about how cookies work and how you can manage your preferences across different websites and services.`
      },
      {
        title: '10. Contact Us',
        content: `If you have any questions about our use of cookies or this Cookie Policy, please contact us:

By email: info@ertheo.com

By mail:
ERTHEO LIMITED
SUITE 140 TEMPLE CHAMBERS
3-7 TEMPLE AVENUE
LONDON, ENGLAND EC4Y 0DA

We are happy to answer any questions you may have about how we use cookies and how you can manage your preferences.`
      }
    ]
  },
  es: {
    title: 'Política de Cookies',
    lastUpdated: 'Enero 2023',
    sections: [
      {
        title: '1. ¿Qué Son las Cookies?',
        content: `Las cookies son pequeños fragmentos de texto enviados por su navegador web desde un sitio web que visita. Un archivo de cookie se almacena en su navegador web y permite que el Servicio o un tercero lo reconozca y haga su próxima visita más fácil y el Servicio más útil para usted.

Las cookies pueden ser "persistentes" o de "sesión". Las cookies persistentes permanecen en su ordenador personal o dispositivo móvil cuando se desconecta, mientras que las cookies de sesión se eliminan tan pronto como cierra su navegador web.

Cuando accede a nuestro sitio web, podemos colocar una serie de cookies en su navegador. Utilizamos cookies para los siguientes propósitos: habilitar ciertas funciones del servicio, proporcionar análisis, almacenar sus preferencias y permitir la entrega de anuncios.`
      },
      {
        title: '2. Cómo Usamos las Cookies',
        content: `Utilizamos cookies de sesión y persistentes en el Servicio y utilizamos diferentes tipos de cookies para ejecutar el Servicio:

Cookies Esenciales:
Podemos usar cookies esenciales para autenticar usuarios y prevenir el uso fraudulento de cuentas de usuario. Estas cookies son estrictamente necesarias para proporcionarle servicios disponibles a través de nuestro sitio web y para usar algunas de sus funciones.

Cookies de Análisis:
Podemos usar cookies de análisis para rastrear información sobre cómo se usa el Servicio para poder hacer mejoras. También podemos usar cookies de análisis para probar nuevos anuncios, páginas, funciones o nuevas funcionalidades del Servicio para ver cómo reaccionan nuestros usuarios.

Cookies de Preferencias:
Podemos usar cookies de preferencias para recordar sus preferencias y varias configuraciones. Estas incluyen sus preferencias de idioma, configuración de país y otras opciones de personalización.

Cookies de Marketing:
Podemos usar cookies de marketing para entregar anuncios que sean relevantes para usted. Las cookies de marketing rastrean su actividad en los sitios web para ayudar a los anunciantes a entregar publicidad más relevante o para limitar cuántas veces ve un anuncio.`
      },
      {
        title: '3. Tipos de Cookies que Usamos',
        content: `Los siguientes tipos de cookies pueden usarse cuando visita nuestro sitio web:

Cookies Estrictamente Necesarias:
Estas cookies son esenciales para que navegue por el sitio web y use sus funciones, como acceder a áreas seguras del sitio. Sin estas cookies, los servicios que ha solicitado no pueden proporcionarse.
Ejemplos: Gestión de sesiones, tokens de seguridad, equilibrio de carga

Cookies de Rendimiento:
Estas cookies recopilan información sobre cómo usa nuestro sitio web, por ejemplo, qué páginas visita con más frecuencia. Estos datos pueden usarse para ayudar a optimizar nuestro sitio web y facilitar su navegación.
Ejemplos: Google Analytics, tiempos de carga de páginas, seguimiento de errores

Cookies de Funcionalidad:
Estas cookies permiten que el sitio web recuerde las opciones que hace (como su preferencia de idioma) y proporcione funciones mejoradas y más personales.
Ejemplos: Selección de idioma, personalización de la interfaz de usuario, recordar datos de inicio de sesión

Cookies de Segmentación/Publicidad:
Estas cookies se utilizan para entregar anuncios más relevantes para usted y sus intereses. También se utilizan para limitar el número de veces que ve un anuncio, así como para ayudar a medir la efectividad de las campañas publicitarias.
Ejemplos: Seguimiento de redes sociales, cookies de remarketing, seguimiento de conversiones`
      },
      {
        title: '4. Cookies de Terceros',
        content: `Además de nuestras propias cookies, también podemos usar varias cookies de terceros para informar estadísticas de uso del Servicio, entregar anuncios en y a través del Servicio, etc.

Los servicios de terceros que usamos y que pueden colocar cookies incluyen:

Google Analytics:
Usamos Google Analytics para analizar el uso de nuestro sitio web. Google Analytics recopila información sobre el uso del sitio web mediante cookies. La información recopilada relacionada con nuestro sitio web se utiliza para crear informes sobre el uso de nuestro sitio web.

Plataformas de Redes Sociales:
Nuestro sitio web puede incluir funciones de redes sociales, como el botón Me gusta de Facebook. Estas funciones pueden recopilar su dirección IP, qué página está visitando en nuestro sitio, y pueden establecer una cookie para permitir que la función funcione correctamente.

Procesadores de Pago:
Cuando realiza una reserva, nuestro procesador de pagos puede establecer cookies para habilitar la funcionalidad de pago y prevenir el fraude.

Socios de Marketing:
Podemos trabajar con socios de publicidad y marketing que pueden establecer cookies en nuestro sitio web para ayudarnos a entregar anuncios relevantes para usted.`
      },
      {
        title: '5. Gestión de Sus Preferencias de Cookies',
        content: `Tiene varias opciones para gestionar las cookies:

Configuración del Navegador:
La mayoría de los navegadores web le permiten controlar las cookies a través de sus preferencias de configuración. Sin embargo, si limita la capacidad de los sitios web para establecer cookies, puede empeorar su experiencia general de usuario, ya que ya no estará personalizada para usted.

Puede averiguar cómo gestionar las cookies en su navegador aquí:
• Chrome: chrome://settings/cookies
• Firefox: about:preferences#privacy
• Safari: Preferencias > Privacidad
• Edge: Configuración > Cookies y permisos del sitio

Herramientas de Exclusión:
Puede optar por no participar en la publicidad basada en intereses visitando:
• Digital Advertising Alliance: http://optout.aboutads.info/
• Network Advertising Initiative: http://optout.networkadvertising.org/
• European Interactive Digital Advertising Alliance: http://www.youronlinechoices.eu/

No Rastrear:
Algunos navegadores incluyen la capacidad de transmitir señales de "No Rastrear". Hacemos todo lo posible por honrar estas señales donde sea técnicamente factible.

Consentimiento de Cookies:
Cuando visite nuestro sitio web por primera vez, se le pedirá que consienta nuestro uso de cookies. Puede retirar su consentimiento en cualquier momento borrando sus cookies y volviendo a visitar nuestro sitio.`
      },
      {
        title: '6. Cookies Específicas que Usamos',
        content: `Aquí hay una lista detallada de las cookies que usamos en nuestro sitio web:

Cookies Esenciales:
• PHPSESSID - Identificador de sesión (Cookie de sesión)
• csrf_token - Token de seguridad para prevenir falsificación de solicitudes (Cookie de sesión)
• cookie_consent - Almacena sus preferencias de cookies (1 año)

Cookies de Análisis:
• _ga - Identificador de Google Analytics (2 años)
• _gid - Identificador de Google Analytics (24 horas)
• _gat - Usado para limitar la tasa de solicitudes (1 minuto)

Cookies de Preferencias:
• language - Almacena su preferencia de idioma (1 año)
• currency - Almacena su preferencia de moneda (1 año)

Cookies de Marketing:
• _fbp - Píxel de Facebook para publicidad (3 meses)
• IDE - Publicidad de Google DoubleClick (1 año)`
      },
      {
        title: '7. Duración de las Cookies',
        content: `El tiempo que una cookie permanece en su dispositivo depende de si es una cookie "persistente" o de "sesión":

Cookies de Sesión:
Las cookies de sesión duran solo mientras su navegador está abierto. Se eliminan automáticamente cuando cierra su navegador.

Cookies Persistentes:
Las cookies persistentes sobreviven después de que se cierra su navegador. Pueden ser utilizadas por el sitio web para reconocer su dispositivo cuando abre su navegador y navega por Internet de nuevo. La duración de las cookies persistentes es establecida por el sitio web y puede variar desde unos pocos minutos hasta varios años.

Establecemos cookies con diferentes períodos de expiración:
• Algunas cookies expiran cuando cierra su navegador
• Algunas cookies expiran después de 24 horas
• Algunas cookies pueden durar hasta 2 años
• Puede eliminar las cookies en cualquier momento a través de la configuración de su navegador`
      },
      {
        title: '8. Actualizaciones de Esta Política',
        content: `Podemos actualizar esta Política de Cookies de vez en cuando para reflejar, por ejemplo, cambios en las cookies que usamos u otras razones operativas, legales o regulatorias.

Por lo tanto, vuelva a visitar esta Política de Cookies regularmente para mantenerse informado sobre nuestro uso de cookies y tecnologías relacionadas.

La fecha en la parte superior de esta Política de Cookies indica cuándo se actualizó por última vez.

Si realizamos cambios materiales a esta política, le notificaremos a través de un aviso en la página principal de nuestro sitio web u otros medios apropiados.`
      },
      {
        title: '9. Más Información Sobre Cookies',
        content: `Si desea obtener más información sobre las cookies en general y cómo gestionarlas, visite los siguientes recursos:

• All About Cookies: https://www.allaboutcookies.org/
• Your Online Choices: https://www.youronlinechoices.com/
• Network Advertising Initiative: https://www.networkadvertising.org/

Estos recursos proporcionan información completa sobre cómo funcionan las cookies y cómo puede gestionar sus preferencias en diferentes sitios web y servicios.`
      },
      {
        title: '10. Contáctenos',
        content: `Si tiene alguna pregunta sobre nuestro uso de cookies o esta Política de Cookies, contáctenos:

Por correo electrónico: info@ertheo.com

Por correo postal:
ERTHEO LIMITED
SUITE 140 TEMPLE CHAMBERS
3-7 TEMPLE AVENUE
LONDON, ENGLAND EC4Y 0DA

Estaremos encantados de responder cualquier pregunta que pueda tener sobre cómo usamos las cookies y cómo puede gestionar sus preferencias.`
      }
    ]
  },
  fr: {
    title: 'Politique de Cookies',
    lastUpdated: 'Janvier 2023',
    sections: [
      {
        title: '1. Que Sont les Cookies?',
        content: `Les cookies sont de petits morceaux de texte envoyés par votre navigateur web depuis un site web que vous visitez. Un fichier cookie est stocké dans votre navigateur web et permet au Service ou à un tiers de vous reconnaître et de rendre votre prochaine visite plus facile et le Service plus utile pour vous.

Les cookies peuvent être "persistants" ou de "session". Les cookies persistants restent sur votre ordinateur personnel ou appareil mobile lorsque vous vous déconnectez, tandis que les cookies de session sont supprimés dès que vous fermez votre navigateur web.

Lorsque vous accédez à notre site web, nous pouvons placer un certain nombre de cookies dans votre navigateur. Nous utilisons des cookies aux fins suivantes: activer certaines fonctions du service, fournir des analyses, stocker vos préférences et permettre la diffusion de publicités.`
      },
      {
        title: '2. Comment Nous Utilisons les Cookies',
        content: `Nous utilisons à la fois des cookies de session et des cookies persistants sur le Service et nous utilisons différents types de cookies pour exécuter le Service:

Cookies Essentiels:
Nous pouvons utiliser des cookies essentiels pour authentifier les utilisateurs et prévenir l'utilisation frauduleuse des comptes utilisateurs. Ces cookies sont strictement nécessaires pour vous fournir les services disponibles via notre site web et pour utiliser certaines de ses fonctionnalités.

Cookies d'Analyse:
Nous pouvons utiliser des cookies d'analyse pour suivre les informations sur la façon dont le Service est utilisé afin de pouvoir apporter des améliorations. Nous pouvons également utiliser des cookies d'analyse pour tester de nouvelles publicités, pages, fonctionnalités ou nouvelles fonctionnalités du Service pour voir comment nos utilisateurs réagissent.

Cookies de Préférences:
Nous pouvons utiliser des cookies de préférences pour mémoriser vos préférences et divers paramètres. Ceux-ci incluent vos préférences linguistiques, les paramètres de pays et d'autres options de personnalisation.

Cookies Marketing:
Nous pouvons utiliser des cookies marketing pour diffuser des publicités qui vous sont pertinentes. Les cookies marketing suivent votre activité sur les sites web pour aider les annonceurs à diffuser des publicités plus pertinentes ou pour limiter le nombre de fois que vous voyez une publicité.`
      },
      {
        title: '3. Types de Cookies que Nous Utilisons',
        content: `Les types de cookies suivants peuvent être utilisés lorsque vous visitez notre site web:

Cookies Strictement Nécessaires:
Ces cookies sont essentiels pour que vous puissiez naviguer sur le site web et utiliser ses fonctionnalités, comme l'accès aux zones sécurisées du site. Sans ces cookies, les services que vous avez demandés ne peuvent pas être fournis.
Exemples: Gestion de session, jetons de sécurité, équilibrage de charge

Cookies de Performance:
Ces cookies collectent des informations sur la façon dont vous utilisez notre site web—par exemple, quelles pages vous visitez le plus souvent. Ces données peuvent être utilisées pour aider à optimiser notre site web et faciliter votre navigation.
Exemples: Google Analytics, temps de chargement des pages, suivi des erreurs

Cookies de Fonctionnalité:
Ces cookies permettent au site web de mémoriser les choix que vous faites (comme votre préférence linguistique) et de fournir des fonctionnalités améliorées et plus personnelles.
Exemples: Sélection de la langue, personnalisation de l'interface utilisateur, mémorisation des détails de connexion

Cookies de Ciblage/Publicité:
Ces cookies sont utilisés pour diffuser des publicités plus pertinentes pour vous et vos intérêts. Ils sont également utilisés pour limiter le nombre de fois que vous voyez une publicité ainsi que pour aider à mesurer l'efficacité des campagnes publicitaires.
Exemples: Suivi des réseaux sociaux, cookies de remarketing, suivi des conversions`
      },
      {
        title: '4. Cookies Tiers',
        content: `En plus de nos propres cookies, nous pouvons également utiliser divers cookies tiers pour rapporter les statistiques d'utilisation du Service, diffuser des publicités sur et via le Service, etc.

Les services tiers que nous utilisons et qui peuvent placer des cookies incluent:

Google Analytics:
Nous utilisons Google Analytics pour analyser l'utilisation de notre site web. Google Analytics recueille des informations sur l'utilisation du site web au moyen de cookies. Les informations recueillies relatives à notre site web sont utilisées pour créer des rapports sur l'utilisation de notre site web.

Plateformes de Réseaux Sociaux:
Notre site web peut inclure des fonctionnalités de réseaux sociaux, comme le bouton J'aime de Facebook. Ces fonctionnalités peuvent collecter votre adresse IP, quelle page vous visitez sur notre site, et peuvent définir un cookie pour permettre à la fonctionnalité de fonctionner correctement.

Processeurs de Paiement:
Lorsque vous effectuez une réservation, notre processeur de paiement peut définir des cookies pour activer la fonctionnalité de paiement et prévenir la fraude.

Partenaires Marketing:
Nous pouvons travailler avec des partenaires publicitaires et marketing qui peuvent définir des cookies sur notre site web pour nous aider à vous diffuser des publicités pertinentes.`
      },
      {
        title: '5. Gestion de Vos Préférences de Cookies',
        content: `Vous avez plusieurs options pour gérer les cookies:

Paramètres du Navigateur:
La plupart des navigateurs web vous permettent de contrôler les cookies via leurs préférences de paramètres. Cependant, si vous limitez la capacité des sites web à définir des cookies, vous pouvez dégrader votre expérience utilisateur globale, car elle ne sera plus personnalisée pour vous.

Vous pouvez découvrir comment gérer les cookies dans votre navigateur ici:
• Chrome: chrome://settings/cookies
• Firefox: about:preferences#privacy
• Safari: Préférences > Confidentialité
• Edge: Paramètres > Cookies et autorisations du site

Outils de Désinscription:
Vous pouvez vous désinscrire de la publicité basée sur les intérêts en visitant:
• Digital Advertising Alliance: http://optout.aboutads.info/
• Network Advertising Initiative: http://optout.networkadvertising.org/
• European Interactive Digital Advertising Alliance: http://www.youronlinechoices.eu/

Ne Pas Suivre:
Certains navigateurs incluent la possibilité de transmettre des signaux "Ne Pas Suivre". Nous faisons de notre mieux pour honorer ces signaux lorsque cela est techniquement faisable.

Consentement aux Cookies:
Lors de votre première visite sur notre site web, il vous sera demandé de consentir à notre utilisation des cookies. Vous pouvez retirer votre consentement à tout moment en effaçant vos cookies et en revisitant notre site.`
      },
      {
        title: '6. Cookies Spécifiques que Nous Utilisons',
        content: `Voici une liste détaillée des cookies que nous utilisons sur notre site web:

Cookies Essentiels:
• PHPSESSID - Identifiant de session (Cookie de session)
• csrf_token - Jeton de sécurité pour prévenir la falsification de requête (Cookie de session)
• cookie_consent - Stocke vos préférences de cookies (1 an)

Cookies d'Analyse:
• _ga - Identifiant Google Analytics (2 ans)
• _gid - Identifiant Google Analytics (24 heures)
• _gat - Utilisé pour limiter le taux de requêtes (1 minute)

Cookies de Préférences:
• language - Stocke votre préférence linguistique (1 an)
• currency - Stocke votre préférence de devise (1 an)

Cookies Marketing:
• _fbp - Pixel Facebook pour la publicité (3 mois)
• IDE - Publicité Google DoubleClick (1 an)`
      },
      {
        title: '7. Durée des Cookies',
        content: `La durée pendant laquelle un cookie reste sur votre appareil dépend de s'il s'agit d'un cookie "persistant" ou de "session":

Cookies de Session:
Les cookies de session ne durent que tant que votre navigateur est ouvert. Ils sont automatiquement supprimés lorsque vous fermez votre navigateur.

Cookies Persistants:
Les cookies persistants survivent après la fermeture de votre navigateur. Ils peuvent être utilisés par le site web pour reconnaître votre appareil lorsque vous ouvrez votre navigateur et naviguez à nouveau sur Internet. La durée des cookies persistants est définie par le site web et peut varier de quelques minutes à plusieurs années.

Nous définissons des cookies avec différentes périodes d'expiration:
• Certains cookies expirent lorsque vous fermez votre navigateur
• Certains cookies expirent après 24 heures
• Certains cookies peuvent durer jusqu'à 2 ans
• Vous pouvez supprimer les cookies à tout moment via les paramètres de votre navigateur`
      },
      {
        title: '8. Mises à Jour de Cette Politique',
        content: `Nous pouvons mettre à jour cette Politique de Cookies de temps en temps afin de refléter, par exemple, des changements dans les cookies que nous utilisons ou pour d'autres raisons opérationnelles, légales ou réglementaires.

Veuillez donc revisiter cette Politique de Cookies régulièrement pour rester informé de notre utilisation des cookies et des technologies associées.

La date en haut de cette Politique de Cookies indique quand elle a été mise à jour pour la dernière fois.

Si nous apportons des modifications importantes à cette politique, nous vous en informerons par un avis sur la page d'accueil de notre site web ou par d'autres moyens appropriés.`
      },
      {
        title: '9. Plus d\'Informations sur les Cookies',
        content: `Si vous souhaitez en savoir plus sur les cookies en général et comment les gérer, visitez les ressources suivantes:

• All About Cookies: https://www.allaboutcookies.org/
• Your Online Choices: https://www.youronlinechoices.com/
• Network Advertising Initiative: https://www.networkadvertising.org/

Ces ressources fournissent des informations complètes sur le fonctionnement des cookies et comment vous pouvez gérer vos préférences sur différents sites web et services.`
      },
      {
        title: '10. Nous Contacter',
        content: `Si vous avez des questions concernant notre utilisation des cookies ou cette Politique de Cookies, veuillez nous contacter:

Par e-mail: info@ertheo.com

Par courrier:
ERTHEO LIMITED
SUITE 140 TEMPLE CHAMBERS
3-7 TEMPLE AVENUE
LONDON, ENGLAND EC4Y 0DA

Nous serons heureux de répondre à toutes les questions que vous pourriez avoir sur la façon dont nous utilisons les cookies et comment vous pouvez gérer vos préférences.`
      }
    ]
  }
};

const CookiePolicyPage = () => {
  const { language } = useLanguage();
  const content = cookieContent[language] || cookieContent.en;

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            {content.title}
          </h1>
          <p className="text-gray-600 mb-8">
            <strong>{language === 'en' ? 'Last Updated:' : language === 'es' ? 'Última Actualización:' : 'Dernière Mise à Jour:'}</strong> {content.lastUpdated}
          </p>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              {content.sections.map((section, index) => (
                <section key={index} className="mb-8">
                  <h2 className="text-xl font-black text-gray-900 mb-4">{section.title}</h2>
                  <div className="text-gray-700 whitespace-pre-line">
                    {section.content}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
